// 加工指示のアイテム/グループ context の解決・突合・行生成をまとめる。
// 本文は HinbanMaster.js から移設したもの。

// アイテム名称マスタ(m_code:0138)。加工指示画面のアイテムタブと同じマスタから名称を引く。
const KAKOUSIJI_ITEM_NAME_MASTER_DATAID = 'm_code:0138';
// 照会済みコード(未応答含む)。サービスは呼び出し毎に生成されるためモジュールで保持する。
const requestedKakousijiItemNameCodes = new Set();

export function createKakousijiGroupContextService(deps) {
    const {
        AGRef,
        DicVal,
        GridApi,
        GridData,
        KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE,
        KAKOUSIJI_QUALITY_BASE_PANEL_TYPE,
        SetGridData,
        entrydataRef,
        extractKakousijiMasterName,
        fnc,
        gridlist,
        isEmpty,
        kakousijiGroupControlState,
        kakousijiGroupControlStateRef,
        kakousijiQualityGroupControlState,
        kakousijiQualityGroupControlStateRef,
        normalizeKakousijiGroupNo,
        normalizeKakousijiText,
        panel,
        pdtItemCodesPanel,
        readKakousijiItemGroupFromAnyRow,
        readKakousijiRowValueIgnoreCase,
        resolveItemMasterItemCode,
        resolveKakousijiColorOptionsForDialog,
        runAfterActiveInputCommit,
        setKakousijiGroupControlState,
        setKakousijiGroupDialogState
    } = deps;

    function isKakousijiGroupManagedPanelType(panelType) {
        const targetType = normalizeKakousijiText(panelType);
        return targetType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE || targetType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE;
    }
    function resolveKakousijiGroupItemContext(row) {
        const context = readKakousijiItemGroupFromAnyRow(row);
        return {
            itemCd: normalizeKakousijiText(DicVal(context, 'itemCd', '')),
            itemName: normalizeKakousijiText(DicVal(context, 'itemName', '')),
            groupNo: normalizeKakousijiGroupNo(DicVal(context, 'groupNo', '')),
            colorCd: normalizeKakousijiText(DicVal(context, 'colorCd', '')),
            hasItemGroupKey: DicVal(context, 'hasItemGroupKey', false) === true
        };
    }
    function rowMatchesKakousijiItemGroup(row, itemCd, groupNo) {
        const context = resolveKakousijiGroupItemContext(row);
        const targetItem = normalizeKakousijiText(itemCd);
        const targetGroup = normalizeKakousijiGroupNo(groupNo);
        return context.hasItemGroupKey === true && targetItem !== '' && context.itemCd === targetItem && context.groupNo === targetGroup;
    }
    function getKakousijiRowsByContext(panelType, itemCd, groupNo) {
        const targetType = normalizeKakousijiText(panelType);
        const targetItem = normalizeKakousijiText(itemCd);
        const targetGroup = normalizeKakousijiGroupNo(groupNo);
        if (targetType === '' || targetItem === '') return [];
        return getKakousijiGridRowsByType(targetType).filter((row) => rowMatchesKakousijiItemGroup(row, targetItem, targetGroup));
    }
    function resolveKakousijiGroupSourceRows() {
        const rows = [
            ...getKakousijiGridRowsByType(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE),
            ...getKakousijiGridRowsByType(KAKOUSIJI_QUALITY_BASE_PANEL_TYPE)
        ].filter((row) => !isEmpty(row));
        if (!isEmpty(entrydataRef.current)) rows.push({ ...entrydataRef.current });
        return rows;
    }
    // アイテムコードから名称を解決する。マスタキャッシュに無ければ非同期で取得し、
    // 応答時に選択stateの参照を差し替えて再描画だけ促す(値は変えないため副作用なし)。
    function resolveKakousijiItemMasterName(itemCd) {
        const code = normalizeKakousijiText(itemCd);
        if (code === '' || isEmpty(fnc.current)) return '';
        const cached = fnc.current.getMaster(KAKOUSIJI_ITEM_NAME_MASTER_DATAID, code);
        // サーバの GetMaster は名称を 'name' キーで返す(m_code_name は others 指定時のみ)。
        if (!isEmpty(cached)) return normalizeKakousijiText(DicVal(cached, 'name', DicVal(cached, 'm_code_name', '')));
        if (!requestedKakousijiItemNameCodes.has(code)) {
            requestedKakousijiItemNameCodes.add(code);
            // others に dbcolumn を渡し、グリッドのセル表示(HinbanEntryGridAr)と同じ
            // 'm_code_name' キー付きの形でマスタキャッシュへ格納する。
            // 空 others で格納するとグリッド側が data['m_code_name'] を読めず 'code:undefined' になる。
            const itemNameOthers = [{ columnid: 'PDT_ITMCITEMCD', dbcolumn: 'm_code_name' }];
            fnc.current.getMasterdata(KAKOUSIJI_ITEM_NAME_MASTER_DATAID, code, '', itemNameOthers, null, (data) => {
                if (isEmpty(data) || isEmpty(fnc.current)) return;
                fnc.current.setMaster(data);
                if (typeof setKakousijiGroupControlState === 'function') {
                    setKakousijiGroupControlState((prev) => ({ ...prev }));
                }
            });
        }
        return '';
    }
    function collectKakousijiGroupItemOptions() {
        const map = {};
        resolveKakousijiGroupSourceRows().forEach((row) => {
            const context = resolveKakousijiGroupItemContext(row);
            if (context.itemCd === '') return;
            if (isEmpty(map[context.itemCd])) map[context.itemCd] = { code: context.itemCd, name: context.itemName };
            if (normalizeKakousijiText(map[context.itemCd].name) === '' && context.itemName !== '') map[context.itemCd].name = context.itemName;
        });
        return Object.keys(map).sort().map((key) => {
            const name = normalizeKakousijiText(map[key].name) !== ''
                ? normalizeKakousijiText(map[key].name)
                : resolveKakousijiItemMasterName(key);
            return { code: key, name, label: name === '' ? key : (key + ':' + name) };
        });
    }
    function collectPdtItemDetailItemOptions() {
        const map = {};
        const rows = GridData(pdtItemCodesPanel());
        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const code = normalizeKakousijiText(DicVal(row, 'PDT_ITMCITEMCD', ''));
            if (code === '' || !isEmpty(map[code])) return;
            map[code] = { code, name: '' };
        });
        return Object.keys(map).sort().map((key) => {
            const name = resolveKakousijiItemMasterName(key);
            return { code: key, name, label: name === '' ? key : (key + ':' + name) };
        });
    }
    function getKnownKakousijiGroupNos(itemCd) {
        const targetItem = normalizeKakousijiText(itemCd);
        const values = [];
        resolveKakousijiGroupSourceRows().forEach((row) => {
            const context = resolveKakousijiGroupItemContext(row);
            if (context.itemCd !== targetItem || context.groupNo === '') return;
            if (values.indexOf(context.groupNo) === -1) values.push(context.groupNo);
        });
        return values.sort((a, b) => {
            const an = Number(a);
            const bn = Number(b);
            if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
            return a.localeCompare(b);
        });
    }
    function getKnownKakousijiGroupNosByPanelType(panelType, itemCd) {
        const targetType = normalizeKakousijiText(panelType);
        const targetItem = normalizeKakousijiText(itemCd);
        const values = [];
        if (targetType === '' || targetItem === '') return values;
        getKakousijiGridRowsByType(targetType).forEach((row) => {
            const context = resolveKakousijiGroupItemContext(row);
            if (context.itemCd !== targetItem || context.groupNo === '') return;
            if (values.indexOf(context.groupNo) === -1) values.push(context.groupNo);
        });
        return values.sort((a, b) => {
            const an = Number(a);
            const bn = Number(b);
            if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
            return a.localeCompare(b);
        });
    }
    function getNextUnusedKakousijiGroupNo(itemCd) {
        const used = new Set(getKnownKakousijiGroupNos(itemCd).map((value) => normalizeKakousijiGroupNo(value)).filter((value) => value !== ''));
        for (let i = 1; i <= 99; i++) {
            const candidate = String(i).padStart(2, '0');
            if (!used.has(candidate)) return candidate;
        }
        return '';
    }
    function nextKakousijiDetailRowId(rows) {
        let nextId = 0;
        (Array.isArray(rows) ? rows : []).forEach((row, index) => {
            const raw = Number(DicVal(row, 'rowid', index));
            if (Number.isFinite(raw) && raw >= nextId) nextId = raw + 1;
        });
        return nextId;
    }
    function createKakousijiGroupRow(panelType, itemCd, groupNo) {
        const row = { rowid: '0' };
        if (panelType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) {
            row.SSDZ_ITMCITEMCD = normalizeKakousijiText(itemCd);
            row.SSDZ_FUKUSIZAICOLORGRP = normalizeKakousijiGroupNo(groupNo);
        } else if (panelType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) {
            row.SSDQ_ITMCITEMCD = normalizeKakousijiText(itemCd);
            row.SSDQ_GROUPNO = normalizeKakousijiGroupNo(groupNo);
            row.SSDQ_FUKUSIZAICOLORGRP = normalizeKakousijiGroupNo(groupNo);
        }
        return row;
    }
    function resolveKakousijiGroupSelection() {
        const itemOptions = collectKakousijiGroupItemOptions();
        // 品質表示スコープは副資材とは独立した選択stateを参照する(加工指示画面と同じ分離)
        const controlState = isEmpty(kakousijiQualityGroupControlStateRef.current) ? kakousijiQualityGroupControlState : kakousijiQualityGroupControlStateRef.current;
        const fallbackItem = normalizeKakousijiText(resolveItemMasterItemCode(entrydataRef.current));
        // 選択済みitemCdは、選択肢リストが一時的に空でも維持する(副資材側と同じ理由)。
        const controlItem = normalizeKakousijiText(controlState.itemCd);
        const selectedItem = controlItem !== ''
            ? controlItem
            : (normalizeKakousijiText(DicVal(itemOptions[0], 'code', '')) || fallbackItem);
        const groupOptions = getKnownKakousijiGroupNos(selectedItem);
        const controlGroupNo = normalizeKakousijiGroupNo(controlState.groupNo);
        const selectedGroup = controlGroupNo !== ''
            ? controlGroupNo
            : normalizeKakousijiGroupNo(DicVal(groupOptions, 0, ''));
        return { itemOptions, selectedItem, groupOptions, selectedGroup };
    }
    function resolveKakousijiFukushizaiBaseGroupSelection() {
        const itemOptions = collectPdtItemDetailItemOptions();
        const controlState = isEmpty(kakousijiGroupControlStateRef.current) ? kakousijiGroupControlState : kakousijiGroupControlStateRef.current;
        // 選択済みitemCdは、選択肢リストが一時的に空(グリッド未反映等)でも維持する。
        // 選択肢先頭へ落とすと itemCd='' となり、投影が確定済みの表示値を消してしまう。
        const controlItem = normalizeKakousijiText(controlState.itemCd);
        const selectedItem = controlItem !== ''
            ? controlItem
            : normalizeKakousijiText(DicVal(itemOptions[0], 'code', ''));
        const groupOptions = getKnownKakousijiGroupNosByPanelType(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE, selectedItem);
        const controlGroupNo = normalizeKakousijiGroupNo(controlState.groupNo);
        const selectedGroup = controlGroupNo !== ''
            ? controlGroupNo
            : normalizeKakousijiGroupNo(DicVal(groupOptions, 0, ''));
        return { itemOptions, selectedItem, groupOptions, selectedGroup };
    }
    function resolveKakousijiGroupCopySelection(panelType) {
        const selection = panelType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE
            ? resolveKakousijiFukushizaiBaseGroupSelection()
            : resolveKakousijiGroupSelection();
        const controlState = isEmpty(kakousijiGroupControlStateRef.current) ? kakousijiGroupControlState : kakousijiGroupControlStateRef.current;
        const itemOptions = selection.itemOptions;
        const sourceItem = itemOptions.some((item) => item.code === normalizeKakousijiText(controlState.sourceItemCd))
            ? normalizeKakousijiText(controlState.sourceItemCd)
            : selection.selectedItem;
        const sourceGroupOptions = getKnownKakousijiGroupNosByPanelType(panelType, sourceItem);
        const controlSourceGroupNo = normalizeKakousijiGroupNo(controlState.sourceGroupNo);
        const sourceGroup = controlSourceGroupNo !== ''
            ? controlSourceGroupNo
            : normalizeKakousijiGroupNo(DicVal(sourceGroupOptions, 0, ''));
        return {
            ...selection,
            sourceItem,
            sourceGroupOptions,
            sourceGroup
        };
    }
    function resolveKakousijiActiveGroupSelection(panelType) {
        const targetType = normalizeKakousijiText(panelType);
        if (targetType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) {
            const selection = resolveKakousijiGroupCopySelection(targetType);
            const result = {
                itemCd: normalizeKakousijiText(DicVal(selection, 'selectedItem', '')),
                groupNo: normalizeKakousijiGroupNo(DicVal(selection, 'selectedGroup', ''))
            };
            return result;
        }
        if (targetType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) {
            const selection = resolveKakousijiGroupSelection();
            const result = {
                itemCd: normalizeKakousijiText(DicVal(selection, 'selectedItem', '')),
                groupNo: normalizeKakousijiGroupNo(DicVal(selection, 'selectedGroup', ''))
            };
            return result;
        }
        const emptyResult = { itemCd: '', groupNo: '' };
        return emptyResult;
    }
    function applyKakousijiGroupSelectionToRow(panelType, row) {
        if (isEmpty(row) || !isKakousijiGroupManagedPanelType(panelType)) return false;
        const selection = resolveKakousijiActiveGroupSelection(panelType);
        const targetItem = normalizeKakousijiText(selection.itemCd) || normalizeKakousijiText(resolveItemMasterItemCode(entrydataRef.current));
        const targetGroup = normalizeKakousijiGroupNo(selection.groupNo);
        if (targetItem === '') {
            return false;
        }
        let changed = false;
        if (panelType === KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE) {
            if (normalizeKakousijiText(DicVal(row, 'SSDZ_ITMCITEMCD', '')) === '') {
                row.SSDZ_ITMCITEMCD = targetItem;
                changed = true;
            }
            if (targetGroup !== '' && normalizeKakousijiGroupNo(DicVal(row, 'SSDZ_FUKUSIZAICOLORGRP', '')) === '') {
                row.SSDZ_FUKUSIZAICOLORGRP = targetGroup;
                changed = true;
            }
            return changed;
        }
        if (panelType === KAKOUSIJI_QUALITY_BASE_PANEL_TYPE) {
            if (normalizeKakousijiText(DicVal(row, 'SSDQ_ITMCITEMCD', '')) === '') {
                row.SSDQ_ITMCITEMCD = targetItem;
                changed = true;
            }
            if (targetGroup !== '') {
                if (normalizeKakousijiGroupNo(DicVal(row, 'SSDQ_GROUPNO', '')) === '') {
                    row.SSDQ_GROUPNO = targetGroup;
                    changed = true;
                }
                if (normalizeKakousijiGroupNo(DicVal(row, 'SSDQ_FUKUSIZAICOLORGRP', '')) === '') {
                    row.SSDQ_FUKUSIZAICOLORGRP = targetGroup;
                    changed = true;
                }
            }
        }
        return changed;
    }
    function doesKakousijiGroupSelectionMatchRow(panelType, row) {
        if (!isKakousijiGroupManagedPanelType(panelType)) return true;
        if (isEmpty(row)) return true;
        const selection = resolveKakousijiActiveGroupSelection(panelType);
        const targetItem = normalizeKakousijiText(selection.itemCd);
        const targetGroup = normalizeKakousijiGroupNo(selection.groupNo);
        const context = resolveKakousijiGroupItemContext(row);
        let matched = false;
        if (context.hasItemGroupKey !== true) {
            matched = targetItem === '' && targetGroup === '' && context.itemCd === '' && context.groupNo === '';
        } else if (targetItem === '') {
            matched = context.itemCd === '' && context.groupNo === '';
        } else {
            matched = rowMatchesKakousijiItemGroup(row, targetItem, targetGroup);
        }
        return matched;
    }
    function buildKakousijiGroupFilterVersion(panelType) {
        if (!isKakousijiGroupManagedPanelType(panelType)) return '';
        const selection = resolveKakousijiActiveGroupSelection(panelType);
        return [
            normalizeKakousijiText(panelType),
            normalizeKakousijiText(selection.itemCd),
            normalizeKakousijiGroupNo(selection.groupNo)
        ].join('|');
    }
    function handleKakousijiGroupAdd(panelType, itemCd) {
        runAfterActiveInputCommit(() => {
            const targetItem = normalizeKakousijiText(itemCd) || normalizeKakousijiText(resolveItemMasterItemCode(entrydataRef.current));
            if (targetItem === '') {
                fnc.current.warnmsg('副資材アイテムを選択してください');
                return;
            }
            const panelInfo = resolveKakousijiGridPanelByType(panelType);
            if (isEmpty(panelInfo)) return;
            const nextGroupNo = getNextUnusedKakousijiGroupNo(targetItem);
            if (nextGroupNo === '') {
                fnc.current.warnmsg('追加できるグループがありません');
                return;
            }
            setKakousijiGroupDialogState({
                open: true,
                mode: 'add',
                panelType,
                title: 'Group Add',
                itemCd: targetItem,
                groupNo: nextGroupNo,
                colorOptions: resolveKakousijiColorOptionsForDialog(panelType, targetItem, '')
            });
        });
    }
    function resolveKakousijiSourceFieldName(row, fieldId, rawValue = '') {
        if (isEmpty(row) || normalizeKakousijiText(fieldId) === '') return '';
        const candidates = [
            fieldId + '_nm',
            fieldId + '_NM',
            fieldId + '_name',
            fieldId + '_NAME',
            fieldId + '_display',
            fieldId + '_DISPLAY'
        ];
        for (let i = 0; i < candidates.length; i++) {
            const value = normalizeKakousijiText(readKakousijiRowValueIgnoreCase(row, candidates[i]));
            if (value !== '') return value;
        }
        return extractKakousijiMasterName(rawValue);
    }
    function resolveKakousijiGridPanelByType(type) {
        const typeKey = normalizeKakousijiText(type);
        if (typeKey === '') return null;
        const hit = (Array.isArray(gridlist) ? gridlist : []).find((item) => normalizeKakousijiText(DicVal(item, 'type', '')) === typeKey);
        if (!isEmpty(hit)) return hit;
        const fallback = (Array.isArray(panel) ? panel : []).find((item) => normalizeKakousijiText(DicVal(item, 'type', '')) === typeKey);
        return isEmpty(fallback) ? null : fallback;
    }
    function getKakousijiGridRowsByType(type) {
        const panelInfo = resolveKakousijiGridPanelByType(type);
        if (isEmpty(panelInfo)) return [];
        const api = GridApi(panelInfo);
        if (!isEmpty(api) && !isEmpty(AGRef.current) && typeof AGRef.current.GetDatasource === 'function') {
            const rows = AGRef.current.GetDatasource(api);
            return Array.isArray(rows) ? rows : [];
        }
        const rows = GridData(panelInfo);
        return Array.isArray(rows) ? rows : [];
    }
    function stopKakousijiGridEditing(panelInfo) {
        if (isEmpty(panelInfo)) return;
        const api = GridApi(panelInfo);
        if (!isEmpty(api) && typeof api.stopEditing === 'function') api.stopEditing();
    }
    function syncKakousijiGridRowsFromApi(panelInfo) {
        if (isEmpty(panelInfo)) return [];
        const api = GridApi(panelInfo);
        if (!isEmpty(api) && !isEmpty(AGRef.current) && typeof AGRef.current.GetDatasource === 'function') {
            const rows = AGRef.current.GetDatasource(api);
            if (Array.isArray(rows)) {
                SetGridData(panelInfo, rows);
                return rows;
            }
        }
        const rows = GridData(panelInfo);
        return Array.isArray(rows) ? rows : [];
    }

    return {
        isKakousijiGroupManagedPanelType,
        resolveKakousijiGroupItemContext,
        rowMatchesKakousijiItemGroup,
        getKakousijiRowsByContext,
        resolveKakousijiGroupSourceRows,
        collectKakousijiGroupItemOptions,
        collectPdtItemDetailItemOptions,
        getKnownKakousijiGroupNos,
        getKnownKakousijiGroupNosByPanelType,
        getNextUnusedKakousijiGroupNo,
        nextKakousijiDetailRowId,
        createKakousijiGroupRow,
        resolveKakousijiGroupSelection,
        resolveKakousijiFukushizaiBaseGroupSelection,
        resolveKakousijiGroupCopySelection,
        resolveKakousijiActiveGroupSelection,
        applyKakousijiGroupSelectionToRow,
        doesKakousijiGroupSelectionMatchRow,
        buildKakousijiGroupFilterVersion,
        handleKakousijiGroupAdd,
        resolveKakousijiSourceFieldName,
        resolveKakousijiGridPanelByType,
        getKakousijiGridRowsByType,
        stopKakousijiGridEditing,
        syncKakousijiGridRowsFromApi
    };
}
